import React, { useState, useEffect, createElement } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Mail, Plus, Gift, Calendar, Heart, LogIn, UserPlus, Send, UserCircle, X, Check, Package, AlertCircle, Clock, ShoppingBag, Menu, LogOut } from 'lucide-react';
import { createCardOrder } from './lib/lob';
import { CardSelector } from './components/CardSelector';
import { SignatureUpload } from './components/SignatureUpload';
import { EditProfile } from './components/EditProfile';
import { ContactForm } from './components/ContactForm';
import { ResetPassword } from './components/ResetPassword';
import { useAuth } from './hooks/useAuth';
import { getContacts, addContact, createOrder, deleteContact } from './lib/db';
import { supabase } from './lib/supabase';
import { EditContact } from './components/EditContact';
import { BulkCardSender } from './components/BulkCardSender';
import { CreditBundles } from './components/CreditBundles';
import { getSubscriptionStatus, handleStripeReturn, createCheckoutSession } from './lib/stripe';
import { UpcomingEvents } from './components/UpcomingEvents';

interface Contact {
  id: string;
  name: string;
  email: string;
  birthday: string | null;
  anniversary: string | null;
  card_preference: string;
  relationship: string;
  gender: 'male' | 'female' | null;
  address_line1?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  address_country?: string;
}

interface CardOrder {
  id: string;
  lob_order_id: string;
  status: string;
  expected_delivery_date: string;
  credits_used: number;
  recipient_name: string;
  recipient_address: string;
  created_at: string;
  contact_id?: string;
  card_id?: string;
  message?: string;
}

interface OrderHistoryResponse {
  data: CardOrder[];
  total_count: number;
}

interface UserProfile {
  first_name: string;
  last_name: string;
  email: string;
  signature_url: string | null;
  address_line1?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
}

interface EditProfileProps {
  onClose: () => void;
  onUpdate: () => void;
}

interface CreditPurchase {
  id: string;
  credits_amount: number;
  credits_remaining: number;
  purchase_date: string;
  expiration_date: string;
  days_remaining: number;
}

interface UserCreditsInfo {
  total_credits: number;
  purchases: CreditPurchase[];
}

function Sidebar({ 
  isOpen, 
  onClose,
  setShowProfileEdit,
  setShowOrderHistory,
  fetchOrderHistory,
  setShowCreditBundles,
  logout
}: { 
  isOpen: boolean; 
  onClose: () => void;
  setShowProfileEdit: (show: boolean) => void;
  setShowOrderHistory: (show: boolean) => void;
  fetchOrderHistory: () => void;
  setShowCreditBundles: (show: boolean) => void;
  logout: () => void;
}) {
  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold font-display text-brand-secondary">Menu</h2>
            <button
              onClick={onClose}
              className="text-brand-secondary hover:text-brand-primary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <nav className="p-4 space-y-2">
          <button
            onClick={() => {
              setShowProfileEdit(true);
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors p-2"
          >
            <UserCircle className="w-5 h-5" />
            <span>Edit Profile</span>
          </button>
          <button
            onClick={() => {
              setShowOrderHistory(true);
              fetchOrderHistory();
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors p-2"
          >
            <ShoppingBag className="w-5 h-5" />
            <span>Order History</span>
          </button>
          <button
            onClick={() => {
              setShowCreditBundles(true);
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors p-2"
          >
            <Package className="w-5 h-5" />
            <span>Buy Credits</span>
          </button>
          <button
            onClick={() => {
              logout();
              onClose();
            }}
            className="w-full flex items-center gap-2 text-brand-secondary hover:text-brand-primary transition-colors p-2"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </nav>
      </div>
    </>
  );
}

export default function App() {
  const { user, authError, signIn, signUp, logout, resetPassword, resetEmailSent, setResetEmailSent } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showSignatureUpload, setShowSignatureUpload] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedOccasion, setSelectedOccasion] = useState<'Birthday' | 'Anniversary'>('Birthday');
  const [showCardSelector, setShowCardSelector] = useState(false);
  const [cardOrders, setCardOrders] = useState<CardOrder[]>([]);
  const [showCreditBundles, setShowCreditBundles] = useState(false);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [creditDetails, setCreditDetails] = useState<CreditPurchase[]>([]);
  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [orderHistory, setOrderHistory] = useState<CardOrder[]>([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryPage, setOrderHistoryPage] = useState(0);
  const [orderHistoryTotal, setOrderHistoryTotal] = useState(0);
  const ORDER_HISTORY_LIMIT = 5;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showUpcomingEvents, setShowUpcomingEvents] = useState(false);

  useEffect(() => {
    if (user) {
      const initializeApp = async () => {
        try {
          await Promise.all([
            checkUserProfile().catch(console.error),
            fetchContacts().catch(console.error),
            fetchUserCredits().catch(console.error)
          ]);
        } catch (error) {
          console.error('Error initializing app:', error);
        }
      };
      initializeApp();
    }
  }, [user]);

  useEffect(() => {
    const checkStripeReturn = async () => {
      try {
        const result = await handleStripeReturn();
        if (result?.success) {
          console.log('Stripe purchase successful:', result);
          fetchUserCredits();
        }
      } catch (error) {
        console.error('Error handling Stripe return:', error);
      }
    };

    if (window.location.search.includes('session_id') || 
        window.location.search.includes('success') || 
        window.location.search.includes('canceled')) {
      checkStripeReturn();
    }
  }, []);

  // Add real-time subscription for credit updates
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('credit_updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_updates',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Credit update received:', payload);
          if (payload.new.credits_data && Array.isArray(payload.new.credits_data)) {
            const creditInfo = payload.new.credits_data[0];
            setUserCredits(creditInfo.total_credits || 0);
            setCreditDetails(creditInfo.purchases || []);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  async function fetchContacts() {
    if (!user) return;
    try {
      const contacts = await getContacts(user.id);
      setContacts(contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }

  async function fetchUserCredits() {
    try {
      const { data, error } = await supabase.rpc('get_user_credits_with_expiration');
      
      if (error) throw error;
      
      if (data && Array.isArray(data) && data.length > 0) {
        const creditInfo = data[0];  // Get the first object from the array
        setUserCredits(creditInfo.total_credits || 0);
        setCreditDetails(creditInfo.purchases || []);
        console.log('Credits updated:', creditInfo.total_credits, creditInfo.purchases);
      }
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    await signIn(email, password);
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    const firstName = (form.elements.namedItem('first_name') as HTMLInputElement).value;
    const lastName = (form.elements.namedItem('last_name') as HTMLInputElement).value;

    const { user, error } = await signUp(email, password);
    if (error) {
      console.error('Error signing up:', error);
      return;
    }

    if (user) {
      await supabase.from('users').insert({
        user_id: user.id,
        first_name: firstName,
        last_name: lastName,
        email,
        signature_url: null
      });
    } else {
      console.error('User is null');
    }
  }

  async function handleSendCard(contactId: string, occasion?: 'Birthday' | 'Anniversary') {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;

    // Check if contact has complete address
    if (!contact.address_line1 || !contact.address_city || 
        !contact.address_state || !contact.address_zip) {
      setSelectedContact(contact);
      setShowEditForm(true);
      return;
    }

    // Set the occasion based on parameter or default to birthday if available
    setSelectedOccasion(occasion || (contact.birthday ? 'Birthday' : 'Anniversary'));
    setSelectedContact(contact);
    setShowCardSelector(true);
  }

  async function handleCardSelect(cardId: string, message: string) {
    if (!selectedContact || !user) return;

    try {
      const order = await createOrder({
        contactId: selectedContact.id,
        message,
        occasion: selectedOccasion,
        imageUrl: cardId,
        to: {
          name: selectedContact.name,
          address_line1: selectedContact.address_line1,
          address_city: selectedContact.address_city,
          address_state: selectedContact.address_state,
          address_zip: selectedContact.address_zip,
          address_country: selectedContact.address_country || 'US'
        },
        front: cardId,
        back: message
      });

      setCardOrders([...cardOrders, {
        id: order.id,
        lob_order_id: order.id,
        status: 'sent',
        expected_delivery_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
        credits_used: 1,
        recipient_name: selectedContact.name,
        recipient_address: `${selectedContact.address_line1}, ${selectedContact.address_city}, ${selectedContact.address_state} ${selectedContact.address_zip}`,
        created_at: new Date().toISOString(),
        contact_id: selectedContact.id,
        card_id: cardId,
        message
      }]);

      setShowCardSelector(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error creating card order:', error);
    }
  }

  async function checkUserProfile() {
    if (!user) return;
    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error || !profile || !profile.first_name || !profile.last_name || !profile.signature_url) {
      setShowProfileEdit(true);
    }
    setUserProfile(profile);
  }

  // Add a function to format the date
  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  // Add a credit details modal
  const CreditDetailsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-md my-8">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg">
          <h2 className="text-xl font-semibold font-display text-brand-secondary flex items-center gap-2">
            <Package className="w-5 h-5" />
            Your Credits
          </h2>
          <button
            onClick={() => setShowCreditDetails(false)}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Total Available Credits: {userCredits}</h3>
            <p className="text-sm text-gray-600">
              Credits are consumed in the order they expire, oldest first.
            </p>
          </div>

          {creditDetails.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-md font-semibold">Credit Expiration Schedule:</h3>
              <div className="max-h-[60vh] overflow-y-auto pr-2">
                {creditDetails.map((purchase) => (
                  <div key={purchase.id} className="border rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">{purchase.credits_remaining}</span> of {purchase.credits_amount} credits remaining
                      </div>
                      <div className={`text-sm ${purchase.days_remaining < 30 ? 'text-red-500' : 'text-gray-500'}`}>
                        {purchase.days_remaining <= 0 ? (
                          <span className="flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Expires today</span>
                        ) : (
                          <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> {purchase.days_remaining} days left</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Purchased: {formatDate(purchase.purchase_date)} • 
                      Expires: {formatDate(purchase.expiration_date)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500">You don't have any active credits.</p>
              <button
                onClick={() => {
                  setShowCreditDetails(false);
                  setShowCreditBundles(true);
                }}
                className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              >
                Purchase Credits
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Add a new function to fetch order history
  async function fetchOrderHistory(page = 0) {
    try {
      setOrderHistoryLoading(true);
      const offset = page * ORDER_HISTORY_LIMIT;
      
      const { data, error } = await supabase.rpc(
        'get_user_orders',
        {
          p_limit: ORDER_HISTORY_LIMIT,
          p_offset: offset
        }
      );
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setOrderHistory(data);
        setOrderHistoryTotal(data[0].total_count || 0);
      } else {
        setOrderHistory([]);
        setOrderHistoryTotal(0);
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
    } finally {
      setOrderHistoryLoading(false);
    }
  }

  // Create an Order History Modal component
  const OrderHistoryModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold font-display text-brand-secondary flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Order History
          </h2>
          <button
            onClick={() => setShowOrderHistory(false)}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-brand-secondary mb-6">
            View your past orders and credit usage history.
          </p>

          {orderHistoryLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading order history...</p>
            </div>
          ) : orderHistory.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">You haven't sent any cards yet.</p>
              <button
                onClick={() => {
                  setShowOrderHistory(false);
                  if (contacts.length > 0) {
                    setSelectedContact(contacts[0]);
                    setShowCardSelector(true);
                  } else {
                    setShowAddForm(true);
                  }
                }}
                className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              >
                Send Your First Card
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expected Delivery
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Credits Used
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderHistory.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{order.recipient_name}</div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px]">{order.recipient_address}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.credits_used}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {orderHistoryTotal > ORDER_HISTORY_LIMIT && (
                <div className="flex justify-between items-center mt-6">
                  <div className="text-sm text-gray-500">
                    Showing {orderHistoryPage * ORDER_HISTORY_LIMIT + 1} to {Math.min((orderHistoryPage + 1) * ORDER_HISTORY_LIMIT, orderHistoryTotal)} of {orderHistoryTotal} orders
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const newPage = Math.max(0, orderHistoryPage - 1);
                        setOrderHistoryPage(newPage);
                        fetchOrderHistory(newPage);
                      }}
                      disabled={orderHistoryPage === 0}
                      className="px-3 py-1 bg-gray-100 text-brand-secondary rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        const newPage = orderHistoryPage + 1;
                        setOrderHistoryPage(newPage);
                        fetchOrderHistory(newPage);
                      }}
                      disabled={(orderHistoryPage + 1) * ORDER_HISTORY_LIMIT >= orderHistoryTotal}
                      className="px-3 py-1 bg-gray-100 text-brand-secondary rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Wrap the main content in Routes
  return (
    <Routes>
      <Route 
        path="/reset-password" 
        element={<ResetPassword />} 
      />
      <Route
        path="/*"
        element={
          !user ? (
            // Login/Signup page
            <div className="min-h-screen brand-gradient flex items-center justify-center">
              <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                <div className="flex items-center justify-center mb-8">
                  <Mail className="w-12 h-12 text-brand-primary" />
                </div>
                <h1 className="text-3xl font-bold font-display text-brand-secondary text-center mb-2">Cardible</h1>
                <p className="text-xl tagline text-brand-primary text-center mb-8">Personalized cards, effortlessly delivered</p>
                
                <div className="space-y-4">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="username"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Password</label>
                      <input
                        type="password"
                        name="password"
                        required
                        autoComplete="current-password"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="mt-1 text-sm text-brand-primary hover:text-brand-secondary transition-colors"
                      >
                        Forgot your password?
                      </button>
                    </div>
                    {authError && (
                      <p className="text-red-500 text-sm">{authError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-secondary transition-colors"
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </button>
                  </form>

                  {/* Forgot Password Modal */}
                  {showForgotPassword && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-semibold font-display text-brand-secondary">Reset Password</h3>
                          <button
                            onClick={() => setShowForgotPassword(false)}
                            className="text-brand-secondary hover:text-brand-primary"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {resetEmailSent ? (
                          <div className="text-center py-4">
                            <div className="mb-4 text-green-600">
                              <Check className="w-12 h-12 mx-auto" />
                            </div>
                            <p className="text-brand-secondary mb-4">
                              Password reset instructions have been sent to your email.
                              Please check your inbox and follow the link to reset your password.
                            </p>
                            <button
                              onClick={() => {
                                setShowForgotPassword(false);
                                setResetEmailSent(false);
                              }}
                              className="text-brand-primary hover:text-brand-secondary transition-colors"
                            >
                              Return to login
                            </button>
                          </div>
                        ) : (
                          <form
                            onSubmit={async (e) => {
                              e.preventDefault();
                              const email = (e.target as HTMLFormElement).email.value;
                              await resetPassword(email);
                            }}
                            className="space-y-4"
                          >
                            <p className="text-brand-secondary mb-4">
                              Enter your email address and we'll send you instructions to reset your password.
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-brand-secondary mb-1">
                                Email Address
                              </label>
                              <input
                                type="email"
                                name="email"
                                required
                                autoComplete="username"
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                              />
                            </div>
                            {authError && (
                              <p className="text-red-500 text-sm">{authError}</p>
                            )}
                            <div className="flex justify-end gap-3">
                              <button
                                type="button"
                                onClick={() => setShowForgotPassword(false)}
                                className="px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
                              >
                                Send Reset Link
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-brand-secondary">Or</span>
                    </div>
                  </div>

                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        autoComplete="username"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Password</label>
                      <input
                        type="password"
                        name="password"
                        required
                        autoComplete="new-password"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-secondary">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring focus:ring-brand-accent focus:ring-opacity-50"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-white text-brand-primary px-4 py-2 rounded-lg border border-brand-primary hover:bg-brand-accent transition-colors"
                    >
                      <UserPlus className="w-4 h-4" />
                      Sign Up
                    </button>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            // Main app
            <div className="min-h-screen brand-gradient">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <header className="text-center mb-8 md:mb-12">
                  <div className="flex items-center justify-center mb-4">
                    <Mail className="w-10 h-10 sm:w-12 sm:h-12 text-brand-primary" />
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-secondary mb-2">Cardible</h1>
                  <p className="text-lg sm:text-xl tagline text-brand-primary mb-4">Personalized cards, effortlessly delivered</p>
                  
                  <div className="mt-4 flex items-center justify-between w-full max-w-4xl mx-auto relative">
                    {/* Menu Button - Left */}
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      <Menu className="w-6 h-6" />
                    </button>

                    {/* Calendar Icon - Center */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <button
                        onClick={() => setShowUpcomingEvents(true)}
                        className="text-brand-primary hover:text-brand-secondary transition-colors"
                      >
                        <Calendar className="w-6 h-6" />
                      </button>
                    </div>

                    {/* Credits Display - Right */}
                    <button
                      onClick={() => setShowCreditDetails(true)}
                      className="text-brand-primary hover:text-brand-secondary transition-colors"
                    >
                      <span className="text-sm sm:text-base">{userCredits} Credits</span>
                    </button>
                  </div>
                </header>

                <Sidebar 
                  isOpen={isMenuOpen} 
                  onClose={() => setIsMenuOpen(false)}
                  setShowProfileEdit={setShowProfileEdit}
                  setShowOrderHistory={setShowOrderHistory}
                  fetchOrderHistory={fetchOrderHistory}
                  setShowCreditBundles={setShowCreditBundles}
                  logout={logout}
                />

                <main className="space-y-6 md:space-y-8">
                  {/* Contacts Section */}
                  <div className="card">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                      <h2 className="text-xl sm:text-2xl font-semibold font-display text-brand-secondary">Your Contacts</h2>
                      <button
                        onClick={() => setShowAddForm(true)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-secondary transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm sm:text-base">Add Contact</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          onClick={() => {
                            setSelectedContact(contact);
                            setShowEditForm(true);
                          }}
                          className="bg-white rounded-lg p-4 shadow hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <h3 className="font-semibold text-base sm:text-lg text-brand-secondary">{contact.name}</h3>
                              <p className="text-brand-secondary text-sm">{contact.email}</p>
                              <div className="mt-1 text-sm text-brand-secondary opacity-75">
                                <p>{contact.relationship} • Prefers {contact.card_preference} cards</p>
                                {(!contact.address_line1 || !contact.address_city || 
                                  !contact.address_state || !contact.address_zip) && (
                                  <p className="text-red-500 mt-1">Missing mailing address</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {contact.birthday && (
                                <div title={`Birthday: ${new Date(contact.birthday).toLocaleDateString()}`} className="p-1">
                                  <Gift className="w-5 h-5 text-brand-primary" />
                                </div>
                              )}
                              {contact.anniversary && (
                                <div title={`Anniversary: ${new Date(contact.anniversary).toLocaleDateString()}`} className="p-1">
                                  <Heart className="w-5 h-5 text-brand-primary" />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-center mt-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendCard(contact.id);
                              }}
                              className="px-4 py-2 text-white bg-brand-primary hover:bg-brand-secondary transition-colors rounded-lg"
                            >
                              Send Card
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </main>

                {/* Modals */}
                {showCardSelector && selectedContact && (
                  <CardSelector
                    occasion={selectedOccasion}
                    contact={selectedContact}
                    onSelect={handleCardSelect}
                    onClose={() => {
                      setShowCardSelector(false);
                      setSelectedContact(null);
                    }}
                  />
                )}

                {showAddForm && (
                  <ContactForm
                    mode="add"
                    onClose={() => setShowAddForm(false)}
                    onSubmit={() => {
                      setShowAddForm(false);
                      fetchContacts();
                    }}
                  />
                )}

                {showEditForm && selectedContact && (
                  <ContactForm
                    mode="edit"
                    contact={selectedContact}
                    onClose={() => {
                      setShowEditForm(false);
                      setSelectedContact(null);
                    }}
                    onSubmit={() => {
                      setShowEditForm(false);
                      setSelectedContact(null);
                      fetchContacts();
                    }}
                  />
                )}

                {showCreditBundles && (
                  <CreditBundles
                    onClose={() => {
                      setShowCreditBundles(false);
                      fetchUserCredits();
                    }}
                    onSelectBundle={async (bundle) => {
                      try {
                        await createCheckoutSession('one-time', `price_${bundle}`);
                        setShowCreditBundles(false);
                      } catch (error) {
                        console.error('Error creating checkout session:', error);
                      }
                    }}
                  />
                )}

                {showProfileEdit && userProfile && (
                  <EditProfile
                    user={userProfile}
                    onClose={() => {
                      setShowProfileEdit(false);
                      fetchUserCredits();
                    }}
                    onUpdate={() => {
                      setShowProfileEdit(false);
                      fetchContacts();
                      fetchUserCredits();
                      checkUserProfile(); // Refresh profile data
                    }}
                  />
                )}

                {showOrderHistory && <OrderHistoryModal />}
                {showCreditDetails && <CreditDetailsModal />}

                {showUpcomingEvents && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                      <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-xl font-semibold text-brand-secondary flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Upcoming Events
                        </h3>
                        <button
                          onClick={() => setShowUpcomingEvents(false)}
                          className="text-brand-secondary hover:text-brand-primary transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-4">
                        <UpcomingEvents
                          onSelectEvent={(event) => {
                            const contact = contacts.find(c => c.id === event.contactId);
                            if (contact) {
                              setSelectedContact(contact);
                              setSelectedOccasion(event.occasion);
                              setShowCardSelector(true);
                            }
                            setShowUpcomingEvents(false);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        }
      />
    </Routes>
  );
}

